import { apiClient } from "@/lib/api-client";

/**
 * The import pipeline's admin API (plan 011).
 *
 * A file of its own rather than more of `catalog-api.service.ts`, which is
 * already the largest service in the app and whose opening paragraph is a
 * promise about how PRODUCT reads are composed. An import run is not a product
 * read, and folding it in would make that promise harder to check.
 */

export type ImportRunStatus =
	| "staged"
	| "resolving"
	| "resolved"
	| "committing"
	| "committed"
	| "reverted"
	| "failed"
	| "discarded";

export type ImportMatchStatus =
	| "pending"
	| "exact"
	| "strong"
	| "ambiguous"
	| "unmatched"
	| "new"
	| "error";

export type ImportAction = "create" | "update" | "price_only" | "skip";

/**
 * A source row as a WRITE returns it.
 *
 * `createSource` and `updateSource` return the inserted or updated row with no
 * brand join -- only `listSources` maps `brandName` on. Typing the write results
 * as `ImportSource` claimed a field the server never sends, which costs nothing
 * while both callers discard the value and breaks the moment anyone writes the
 * obvious optimistic cache update: the dashboard would render "(no brand)" for a
 * source that has one, and the type would not object.
 */
export type ImportSourceRow = Omit<ImportSource, "brandName">;

export interface ImportSource {
	id: string;
	name: string;
	kind: string;
	brandId: string | null;
	brandName: string | null;
	defaultBasis: string;
	refreshCadenceDays: number;
	/** Which extractor reads this source's documents; null means the CSV reader. */
	parserKey: string | null;
	url: string | null;
	gateType: string;
	notes: string | null;
	exactMatchEnabled: boolean;
	lastHarvestedAt: string | null;
	nextDueAt: string | null;
	isActive: boolean;
}

/**
 * The six fields registering a source requires, mirroring what the seeder
 * writes. `gateType` is deliberately absent: the column defaults to `none` and
 * no seeded row sets it.
 */
export interface CreateSourceInput {
	name: string;
	brandId: string;
	kind: string;
	defaultBasis: string;
	refreshCadenceDays: number;
	parserKey?: string | null;
	url?: string | null;
	notes?: string | null;
}

export type UpdateSourceInput = Partial<CreateSourceInput> & {
	isActive?: boolean;
};

export async function createImportSource(
	input: CreateSourceInput,
): Promise<ImportSourceRow> {
	const response = await apiClient.post("/api/v1/admin/catalog/sources", input);
	return response.data.data as ImportSourceRow;
}

/**
 * Edit a source.
 *
 * The caller sends only what MOVED -- the same rule the brand editor and the
 * category dialog follow. A PATCH carrying every field writes values the
 * operator never looked at, and the audit trail then records a cadence change
 * as a change to eight columns.
 */
export async function updateImportSource(
	id: string,
	patch: UpdateSourceInput,
): Promise<ImportSourceRow> {
	const response = await apiClient.patch(
		`/api/v1/admin/catalog/sources/${encodeURIComponent(id)}`,
		patch,
	);
	return response.data.data as ImportSourceRow;
}

export interface ImportRun {
	id: string;
	sourceId: string | null;
	sourceName: string | null;
	fileName: string | null;
	status: ImportRunStatus;
	basis: string | null;
	effectiveFrom: string | null;
	columnMapping: Record<string, string> | null;
	header: string[] | null;
	rowsTotal: number;
	rowsNew: number;
	rowsChanged: number;
	rowsUnchanged: number;
	rowsUnmatched: number;
	/**
	 * Rows held back until somebody maps what the source called them (plan 013
	 * U2). Split out of `rowsUnmatched`, so the tiles only add up to
	 * `rowsTotal` if this one is shown too.
	 */
	rowsNeedMapping: number;
	rowsErrored: number;
	committedAt: string | null;
	revertedAt: string | null;
	createdAt: string;
}

export interface ImportCandidate {
	productId: string;
	name: string;
	brandSku: string | null;
	tier: string;
}

/** Mirrors `importHoldReasonEnum`, pinned by `import-contract.test.ts`. */
export type ImportHoldReason =
	| "unmapped_phrase"
	| "declined_phrase"
	| "no_category"
	| "no_unit";

export interface ImportRow {
	id: string;
	lineNo: number;
	rawText: string;
	normalized: Record<string, unknown> | null;
	matchStatus: ImportMatchStatus;
	matchedProductId: string | null;
	matchConfidence: number | null;
	candidates: ImportCandidate[] | null;
	action: ImportAction | null;
	diff: Record<string, { from: unknown; to: unknown }> | null;
	error: string | null;
	/**
	 * Why this row is not going in, as a value rather than as a sentence.
	 *
	 * `error` carries the same fact in words, for the operator. Branch on this,
	 * never on that: the admin used to substring-match the sentence, so a copy
	 * edit on the server silently removed a control from the rows it was the
	 * remedy for. `import-contract.test.ts` pins these values to the server's.
	 *
	 * Null when the row is not held -- including a row that IS going in.
	 */
	holdReason: ImportHoldReason | null;
	reviewedBy: string | null;
	reviewedAt: string | null;
}

/** One phrase a run is held back on, counted over the whole run. */
export interface HeldBackPhrase {
	/** The source's own words, or null for rows held back for another reason. */
	sourceCategory: string | null;
	rows: number;
}

export interface ImportRunDetail {
	run: ImportRun;
	rows: ImportRow[];
	total: number;
	/**
	 * Present only when the caller asked for it.
	 *
	 * Counted over the whole run, not over `rows` -- which is one page. The
	 * mapping editor orders phrases by these counts, and ordering by a page
	 * would put the wrong phrase in front of the operator.
	 */
	heldBackPhrases?: HeldBackPhrase[];
}

/**
 * What one source's own category words mean in our taxonomy.
 *
 * Stored per SOURCE, not globally: the same words mean different things to
 * different brands, and a shared table would let one brand's correction
 * silently re-file another brand's products.
 */
export interface SourceCategoryMapping {
	id: string;
	/** The source's own words, as the document printed them. */
	sourceCategory: string;
	/**
	 * Null where the phrase is DECLINED -- somebody decided these words are not a
	 * category at all. A phrase nobody has looked at has no mapping row, so it is
	 * simply absent from this list; the two are different answers.
	 */
	categoryId: string | null;
	categorySlug: string | null;
	categoryName: string | null;
	createdAt: string;
}

export async function listSourceCategoryMappings(
	sourceId: string,
): Promise<SourceCategoryMapping[]> {
	const response = await apiClient.get(
		`/api/v1/admin/catalog/sources/${encodeURIComponent(sourceId)}/category-mappings`,
	);
	return response.data.data as SourceCategoryMapping[];
}

/**
 * PUT, not POST: the operator's action is "these words mean that category",
 * which is the same request whether or not a mapping already exists. Mapping
 * the same phrase twice is not an error worth refusing, and re-pointing a wrong
 * one is routine.
 */
export async function upsertSourceCategoryMapping(
	sourceId: string,
	/** `categoryId: null` records a decline: these words are not a category. */
	input: { sourceCategory: string; categoryId: string | null },
): Promise<SourceCategoryMapping> {
	const response = await apiClient.put(
		`/api/v1/admin/catalog/sources/${encodeURIComponent(sourceId)}/category-mappings`,
		input,
	);
	return response.data.data as SourceCategoryMapping;
}

export async function listImportSources(): Promise<ImportSource[]> {
	const response = await apiClient.get("/api/v1/admin/catalog/sources");
	return response.data.data as ImportSource[];
}

/** A source as the dashboard's work queue shows it. */
export interface WorkQueueSource {
	id: string;
	name: string;
	gateType: string;
	lastHarvestedAt: string | null;
	nextDueAt: string | null;
}

/** An import run an operator started and has not finished. */
export interface WaitingImportRun {
	id: string;
	sourceName: string | null;
	status: "staged" | "resolved";
	createdAt: string;
	rowsTotal: number;
	/** Rows with no product decided yet. */
	rowsUnmatched: number;
	/** Rows held back until their category words are mapped. */
	rowsNeedMapping: number;
}

export interface ImportWorkQueue {
	/** The first few due sources; `dueTotal` is how many are due in all. */
	due: WorkQueueSource[];
	dueTotal: number;
	gated: WorkQueueSource[];
	/** The newest few waiting runs; `waitingTotal` is how many wait in all. */
	waitingRuns: WaitingImportRun[];
	waitingTotal: number;
}

/** The dashboard's import sections (issue #255). Costs `admin:catalog:import`. */
export async function getImportWorkQueue(): Promise<ImportWorkQueue> {
	const response = await apiClient.get("/api/v1/admin/catalog/work-queue");
	return response.data.data as ImportWorkQueue;
}

/** How many runs one page of the imports list holds (the server's default). */
export const IMPORT_RUNS_PAGE = 50;

export async function listImportRuns(
	filters: {
		sourceId?: string;
		/** Only runs waiting on an operator: staged or resolved. */
		waiting?: boolean;
		offset?: number;
	} = {},
): Promise<ImportRun[]> {
	const params: Record<string, string | number> = {};
	if (filters.sourceId) params.sourceId = filters.sourceId;
	if (filters.waiting) params.status = "waiting";
	if (filters.offset) params.offset = filters.offset;
	const response = await apiClient.get("/api/v1/admin/catalog/imports", {
		params: Object.keys(params).length > 0 ? params : undefined,
	});
	return response.data.data as ImportRun[];
}

/** The sources due for a refresh, soonest first -- the work queue's rule. */
export async function listDueImportSources(): Promise<ImportSource[]> {
	const response = await apiClient.get("/api/v1/admin/catalog/sources", {
		params: { due: 1 },
	});
	return response.data.data as ImportSource[];
}

export async function getImportRun(
	id: string,
	filters: {
		matchStatus?: ImportMatchStatus;
		action?: ImportAction;
		/**
		 * Rows the run is holding back, which `matchStatus` and `action` cannot
		 * express between them: a held-back row is defined by having no action
		 * but an error, and both of those filters are equality predicates.
		 */
		hold?: "needs_mapping";
		/** Also return each held-back phrase with its count for the whole run. */
		includePhrases?: boolean;
		page?: number;
		perPage?: number;
	} = {},
): Promise<ImportRunDetail> {
	const response = await apiClient.get(
		`/api/v1/admin/catalog/imports/${encodeURIComponent(id)}`,
		{ params: filters },
	);
	return response.data.data as ImportRunDetail;
}

/**
 * Stage a file.
 *
 * `columnMapping` goes as a JSON STRING because the request is multipart -- a
 * `FormData` value is a string or a Blob, and appending an object would send
 * the literal `[object Object]`. The server parses it and answers a validation
 * error naming the field if it cannot.
 *
 * The two-minute timeout is the same reasoning as `uploadImage`: the shared
 * axios client sets none, and a stalled upload leaves the button disabled with
 * no cancel and no error.
 */
export async function uploadImportFile(input: {
	sourceId: string;
	file: File;
	columnMapping?: Record<string, string>;
	effectiveFrom?: string;
}): Promise<{ run: ImportRun; duplicateOfRunId: string | null }> {
	const body = new FormData();
	body.append("file", input.file);
	body.append("sourceId", input.sourceId);
	if (input.columnMapping) {
		body.append("columnMapping", JSON.stringify(input.columnMapping));
	}
	if (input.effectiveFrom) body.append("effectiveFrom", input.effectiveFrom);

	const response = await apiClient.post("/api/v1/admin/catalog/imports", body, {
		timeout: 120_000,
	});
	return response.data.data as {
		run: ImportRun;
		duplicateOfRunId: string | null;
	};
}

export interface ResolveResult {
	rowsNew: number;
	rowsChanged: number;
	rowsUnchanged: number;
	rowsUnmatched: number;
	/** Rows that will not import until a category mapping exists. */
	rowsNeedMapping: number;
	rowsErrored: number;
	/**
	 * Rows that WILL import and are worth a look -- a unit outside the
	 * category's allowed list, say. Deliberately not the same number as
	 * `rowsNeedMapping`: these import, those do not.
	 */
	rowsFlagged: number;
}

/**
 * Run the dry run, optionally saving a corrected column mapping first.
 *
 * ONE CALL, because pressing "Dry run" after editing the columns means both
 * things to an operator. The route runs the mapping write and the three passes
 * in ONE TRANSACTION, so a failure part-way cannot leave the run carrying a
 * mapping its rows were never read with.
 */
export async function resolveImportRun(
	id: string,
	columnMapping?: Record<string, string>,
): Promise<ResolveResult> {
	const response = await apiClient.post(
		`/api/v1/admin/catalog/imports/${encodeURIComponent(id)}/resolve`,
		columnMapping ? { columnMapping } : {},
	);
	return response.data.data as ResolveResult;
}

export type RowDecision =
	| { kind: "same_product"; productId: string }
	| { kind: "new_product" }
	| { kind: "skip" };

export async function reviewImportRow(
	runId: string,
	rowId: string,
	decision: RowDecision,
): Promise<void> {
	await apiClient.post(
		`/api/v1/admin/catalog/imports/${encodeURIComponent(runId)}/rows/${encodeURIComponent(rowId)}/review`,
		decision,
	);
}

export interface CommitResult {
	productsCreated: number;
	productsUpdated: number;
	pricesRecorded: number;
	rowsSkipped: number;
	/** Products whose price projection did not recompute; they quote the old price. */
	pricesStale: string[];
	/** Products this run priced that have a price customers do not see, by why. */
	unpriced: UnpricedSummary;
	/**
	 * Set when the run is dated after its commit: its prices are stored and not
	 * in effect, and the date arriving switches nothing on -- the price sweep,
	 * or a price write to a product on or after that date, does.
	 */
	notYetInEffect: { products: number; effectiveFrom: string } | null;
	/**
	 * Created and updated products the commit handed to the search index, and how
	 * many the index reported writing. Optional: a server older than this field
	 * omits both, and the page then says nothing about search.
	 */
	productsOfferedToIndex?: number;
	productsIndexed?: number;
}

/**
 * Mirrors `UNPRICED_REASONS` in the backend's `import-unpriced.ts`, pinned by
 * `import-contract.test.ts`.
 */
export type UnpricedReason =
	| "no_gst_rate"
	| "rate_not_yet_effective"
	| "no_hsn_code"
	| "basis_not_shown";

export interface UnpricedGroup {
	reason: UnpricedReason;
	hsnCode: string | null;
	/** ISO instant a rate for the HSN code starts, for `rate_not_yet_effective`. */
	rateEffectiveFrom: string | null;
	products: number;
}

export interface UnpricedSummary {
	/** Distinct products; a product in two groups counts once here. */
	products: number;
	groups: UnpricedGroup[];
}

export async function commitImportRun(id: string): Promise<CommitResult> {
	const response = await apiClient.post(
		`/api/v1/admin/catalog/imports/${encodeURIComponent(id)}/commit`,
	);
	return response.data.data as CommitResult;
}

export interface RevertConflict {
	productId: string;
	name: string;
	reason: string;
}

export interface RevertResult {
	productsDeleted: number;
	productsRestored: number;
	pricesCompensated: number;
	conflicts: RevertConflict[];
}

/**
 * Drop a staged or resolved run nobody will commit. Nothing is deleted; the
 * run stops being work.
 */
export async function discardImportRun(id: string): Promise<void> {
	await apiClient.post(
		`/api/v1/admin/catalog/imports/${encodeURIComponent(id)}/discard`,
	);
}

export async function revertImportRun(id: string): Promise<RevertResult> {
	const response = await apiClient.post(
		`/api/v1/admin/catalog/imports/${encodeURIComponent(id)}/revert`,
	);
	return response.data.data as RevertResult;
}
